import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RotateCw, AlertTriangle, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, RedirectLoopDetails } from "@/types/technicalAudit";

interface RedirectLoopCardProps {
	issue: PageIssue;
}

export function RedirectLoopCard({ issue }: RedirectLoopCardProps) {
	const details = (issue.issue_details ?? {}) as RedirectLoopDetails;
	const chain = Array.isArray(details.redirect_chain)
		? details.redirect_chain
		: [];

	// A loop issue can arrive without a captured chain — render a safe summary
	// instead of crashing on .indexOf()/.map() over an undefined chain.
	if (chain.length === 0) {
		return (
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
				<p className="text-sm text-red-700 dark:text-red-400">
					This URL was flagged as part of a redirect loop, but no chain detail
					was captured. Inspect the redirect configuration and break the loop.
				</p>
			</div>
		);
	}

	// Find loop start by matching loop_detected_at URL in chain
	const loopStart = Math.max(0, chain.indexOf(details.loop_detected_at));

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Redirect Loop</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						This URL is part of a redirect loop. The browser will never reach a final page, resulting in an error for users and search engines.
					</p>
				</div>
				<Badge variant="outline" className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">
					{details.redirect_chain.length} hops
				</Badge>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
							<RotateCw className="w-4 h-4 text-red-600 dark:text-red-400" />
						</div>
						Redirect Chain
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<div className="space-y-0">
						{details.redirect_chain.map((url, idx) => {
							const isInLoop = idx >= loopStart;
							const isLast = idx === details.redirect_chain.length - 1;
							return (
								<div key={idx}>
									<div className={cn(
										"p-3 rounded-lg border font-mono text-sm break-all",
										isInLoop
											? "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
											: "bg-muted border-border text-foreground",
									)}>
										<div className="flex items-start gap-2">
											<Badge
												variant="outline"
												className={cn(
													"shrink-0 text-xs",
													isInLoop
														? "bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700"
														: "bg-muted text-muted-foreground border-border",
												)}
											>
												{idx + 1}
											</Badge>
											{url}
											{isInLoop && idx === loopStart && (
												<Badge variant="outline" className="shrink-0 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 text-xs ml-auto">
													Loop starts
												</Badge>
											)}
										</div>
									</div>
									{!isLast && (
										<div className="flex justify-center py-1">
											<ArrowDown className={cn(
												"w-4 h-4",
												isInLoop ? "text-red-400" : "text-gray-300",
											)} />
										</div>
									)}
								</div>
							);
						})}
						{/* Loop-back arrow */}
						<div className="flex justify-center py-2">
							<div className="flex items-center gap-1 text-red-500 dark:text-red-400">
								<RotateCw className="w-4 h-4" />
								<span className="text-xs font-medium">Loops back to #{loopStart + 1}</span>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
